<script setup>
import { useHelpersStore } from "@/aygulyazilim/store/useHelpers"
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useKysTedarikciPerformansStore } from "./useKysTedarikciPerformansStore"

const storeHelpers = useHelpersStore()
const store = useKysTedarikciPerformansStore()
const { t } = useI18n()

const search = ref('')
const page = ref(1)
const itemsPerPage = ref(25)
const isDeleteDialogActive = ref(false)
const kayitToDelete = ref(null)

const kayitlar = computed(() => store.kayitlar)
const totalKayitlar = computed(() => store.totalKayitlar)
const loading = computed(() => store.loading)

const fetchKayitlar = async () => {
  try {
    await store.fetchKayitlar({
      page: page.value,
      limit: itemsPerPage.value,
      search: search.value,
    })
  } catch (error) {
    storeHelpers.toast('error', t('Kayıtlar yüklenemedi.'))
  }
}

const confirmDelete = uuid => {
  kayitToDelete.value = uuid
  isDeleteDialogActive.value = true
}

const deleteKayit = async () => {
  if (!kayitToDelete.value) return
  try {
    await store.deleteKayit(kayitToDelete.value)
    storeHelpers.toast('success', t('Kayıt başarıyla silindi.'))
    isDeleteDialogActive.value = false
    kayitToDelete.value = null
    fetchKayitlar()
  } catch (error) {
    storeHelpers.toast('error', t('İşlem sırasında bir hata oluştu.'))
  }
}

const downloadExcel = async uuid => {
  try {
    storeHelpers.toast('info', t('Excel hazırlanıyor, lütfen bekleyin...'))
    await store.downloadExcel(uuid)
  } catch (error) {
    storeHelpers.toast('error', t('Excel indirilirken bir hata oluştu.'))
  }
}

onMounted(() => {
  fetchKayitlar()
})
</script>

<template>
  <VCard class="pa-4">
    <VCardItem class="pb-2">
      <div class="d-flex flex-column flex-md-row justify-space-between align-md-center gap-4">
        <VCardTitle class="text-h5 font-weight-bold">
          EYS Tedarikçi Performans Değerlendirme
        </VCardTitle>
        <div class="d-flex flex-wrap align-center justify-end gap-2 ms-md-auto">
          <VTextField
            v-model="search"
            density="compact"
            :placeholder="$t('Arama...')"
            style="inline-size: 220px !important; max-inline-size: 220px !important;"
            clearable
            @input="fetchKayitlar"
          />
          <VTooltip :text="$t('Yenile')">
            <template #activator="{ props }">
              <VBtn v-bind="props" icon variant="tonal" color="secondary" @click="fetchKayitlar">
                <VIcon icon="eos-icons--arrow-rotate" />
              </VBtn>
            </template>
          </VTooltip>
          <VBtn v-if="storeHelpers.yetkiKontrol({ k: [1191003] })" color="primary" :to="{ name: 'kys-tedarikci-performans-ekle' }">
            <VIcon icon="tabler-plus" class="me-1" />
            Yeni Kayıt
          </VBtn>
        </div>
      </div>
    </VCardItem>

    <VDivider class="my-4" />

    <VCardText class="pa-0">
      <VDataTableServer
        :items-per-page="itemsPerPage"
        :page="page"
        :items="kayitlar"
        :items-length="totalKayitlar"
        :loading="loading"
        :headers="[
          { title: 'Tedarikçi Adı', key: 'tedarikci_adi' },
          { title: 'Değerlendirme Dönemi', key: 'degerlendirme_donemi' },
          { title: 'Toplam Puan', key: 'toplam_puan', align: 'center' },
          { title: 'Karar', key: 'karar' },
          { title: $t('İşlemler'), key: 'actions', sortable: false, align: 'end' }
        ]"
        @update:items-per-page="val => itemsPerPage = val"
        @update:page="val => page = val"
        @update:options="fetchKayitlar"
      >
        <template #[`item.toplam_puan`]="{ item }">
          <VChip size="small" :color="item.toplam_puan >= 80 ? 'success' : (item.toplam_puan >= 60 ? 'warning' : 'error')" variant="tonal">
            {{ item.toplam_puan }}
          </VChip>
        </template>

        <template #[`item.karar`]="{ item }">
          <VChip size="small" :color="item.karar === 'FİRMA ONAYLI TED. LİST. KALMAYA DEVAM ETSİN' ? 'success' : (item.karar === 'FİRMAYA UYARI YAZISI YAZILSIN' ? 'warning' : 'error')" variant="tonal">
            {{ item.karar || '-' }}
          </VChip>
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end gap-x-2">
            <VBtn v-if="storeHelpers.yetkiKontrol({ k: [1191002] })"
                  icon
                  size="small"
                  variant="text"
                  color="success"
                  @click="downloadExcel(item.uuid)"
            >
              <VTooltip activator="parent" location="top">
                {{ $t('Excel İndir') }}
              </VTooltip>
              <VIcon icon="tabler-download" />
            </VBtn>
            <VBtn v-if="storeHelpers.yetkiKontrol({ k: [1191004] })"
                  icon
                  size="small"
                  variant="text"
                  color="primary"
                  :to="{ name: 'kys-tedarikci-performans-duzelt', params: { uuid: item.uuid } }"
            >
              <VTooltip activator="parent" location="top">
                {{ $t('Düzenle') }}
              </VTooltip>
              <VIcon icon="tabler-pencil" />
            </VBtn>
            <VBtn v-if="storeHelpers.yetkiKontrol({ k: [1191005] })"
                  icon
                  size="small"
                  variant="text"
                  color="error"
                  @click="confirmDelete(item.uuid)"
            >
              <VTooltip activator="parent" location="top">
                {{ $t('Sil') }}
              </VTooltip>
              <VIcon icon="tabler-trash" />
            </VBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCardText>

    <VDialog v-model="isDeleteDialogActive" max-width="450px">
      <VCard>
        <VCardTitle class="text-h5 font-weight-bold pa-4">
          Silme Onayı
        </VCardTitle>
        <VDivider />
        <VCardText class="pa-4">
          Bu kaydı silmek istediğinize emin misiniz?
        </VCardText>
        <VDivider />
        <VCardActions class="pa-4 justify-end gap-2">
          <VBtn color="secondary" variant="tonal" @click="isDeleteDialogActive = false">
            {{ $t('Vazgeç') }}
          </VBtn>
          <VBtn color="error" variant="flat" @click="deleteKayit">
            {{ $t('Sil') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VCard>
</template>
